import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Anchor, Ship, LogIn, LogOut, CalendarDays, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

// Public endpoint – no cookies / no auth token attached.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  no: {
    back: "Tilbake til innlogging",
    kicker: "Kanalbåtene i Telemark",
    title: "Se dagens flåte",
    subtitle:
      "En rask, ekslusiv oversikt over båtene våre – hvem som er ute, hvem som ligger til havn, og hvilke dager som fortsatt er ledige den neste uka.",
    boats_out: "Ute på tur akkurat nå",
    boats_in: "Ligger til havn",
    departing_today: "Går fra havn i dag",
    arriving_today: "Kommer inn i dag",
    week_avail: "Ledighet – neste 7 dager",
    none_out: "Alle båtene ligger ved brygga akkurat nå.",
    none_in: "Alle båtene er ute på tur i dag.",
    none_dep: "Ingen avganger fra havn i dag.",
    none_arr: "Ingen ankomster til havn i dag.",
    available: "Ledig",
    booked: "Booket",
    empty_availability: "Ingen båter tilgjengelige denne uken.",
    lang_switch: "English",
    footer: "Kontakt oss for booking – vi hjelper deg gjerne med å finne rett båt.",
    contact_cta: "Kontakt oss",
    loading: "Laster …",
  },
  en: {
    back: "Back to login",
    kicker: "The Canal Boats of Telemark",
    title: "Today's fleet",
    subtitle:
      "A crisp, exclusive overview of our fleet – who is out cruising, who is at the dock, and which days are still available over the next week.",
    boats_out: "Out cruising right now",
    boats_in: "At the harbour",
    departing_today: "Leaving today",
    arriving_today: "Arriving today",
    week_avail: "Availability – next 7 days",
    none_out: "All boats are at the dock right now.",
    none_in: "All boats are out cruising today.",
    none_dep: "No departures today.",
    none_arr: "No arrivals today.",
    available: "Available",
    booked: "Booked",
    empty_availability: "No boats available this week.",
    lang_switch: "Norsk",
    footer: "Contact us to book – we'll help you find the right boat.",
    contact_cta: "Contact us",
    loading: "Loading …",
  },
};

const fmtDay = (iso, lang) => {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString(lang === "no" ? "nb-NO" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

export default function PublicAvailability() {
  // English is the default language for visitors – switch on demand.
  const [lang, setLang] = useState("en");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = STRINGS[lang];

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/public/availability`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0F1A2B] text-white"
      data-testid="public-availability-page"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
          data-testid="public-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "en" ? "no" : "en")}
            data-testid="public-lang-toggle"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.24em] text-[#ECC974] hover:text-white border border-[#BD984D]/40 rounded-full px-3 py-1.5 transition"
          >
            <Sparkles className="w-3.5 h-3.5" /> {t.lang_switch}
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="text-[10px] uppercase tracking-[0.42em] text-[#ECC974]">
          {t.kicker}
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold leading-tight text-white">
          {t.title}
        </h1>
        <p className="mt-3 max-w-2xl text-slate-200/90 leading-relaxed">
          {t.subtitle}
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-16 space-y-6">
        {loading || !data ? (
          <div className="text-center text-slate-300 py-16">{t.loading}</div>
        ) : (
          <>
            {/* Row 1: Departing today + Arriving today */}
            <div className="grid md:grid-cols-2 gap-5">
              <SectionCard
                icon={<LogOut className="w-5 h-5 text-emerald-300" />}
                label={t.departing_today}
                count={data.departing_today.length}
                tone="emerald"
              >
                {data.departing_today.length === 0 ? (
                  <EmptyText>{t.none_dep}</EmptyText>
                ) : (
                  <BoatChips items={data.departing_today} tone="emerald" />
                )}
              </SectionCard>

              <SectionCard
                icon={<LogIn className="w-5 h-5 text-cyan-300" />}
                label={t.arriving_today}
                count={data.arriving_today.length}
                tone="cyan"
              >
                {data.arriving_today.length === 0 ? (
                  <EmptyText>{t.none_arr}</EmptyText>
                ) : (
                  <BoatChips items={data.arriving_today} tone="cyan" />
                )}
              </SectionCard>
            </div>

            {/* Row 2: Boats out + Boats in */}
            <div className="grid md:grid-cols-2 gap-5">
              <SectionCard
                icon={<Ship className="w-5 h-5 text-[#BD984D]" />}
                label={t.boats_out}
                count={data.boats_out.length}
                tone="gold"
              >
                {data.boats_out.length === 0 ? (
                  <EmptyText>{t.none_out}</EmptyText>
                ) : (
                  <BoatCards items={data.boats_out} />
                )}
              </SectionCard>

              <SectionCard
                icon={<Anchor className="w-5 h-5 text-[#0C6E7E]" />}
                label={t.boats_in}
                count={data.boats_in.length}
                tone="teal"
              >
                {data.boats_in.length === 0 ? (
                  <EmptyText>{t.none_in}</EmptyText>
                ) : (
                  <BoatCards items={data.boats_in} />
                )}
              </SectionCard>
            </div>

            {/* Row 3: Week availability */}
            <SectionCard
              icon={<CalendarDays className="w-5 h-5 text-[#ECC974]" />}
              label={t.week_avail}
              count={data.week_availability.length}
              tone="gold"
            >
              {data.week_availability.length === 0 ? (
                <EmptyText>{t.empty_availability}</EmptyText>
              ) : (
                <WeekGrid data={data} lang={lang} t={t} />
              )}
            </SectionCard>

            {/* Footer CTA */}
            <div className="mt-8 rounded-2xl border border-[#BD984D]/40 bg-gradient-to-r from-[#BD984D]/20 via-[#A57F39]/15 to-[#0C6E7E]/15 backdrop-blur px-8 py-6 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-slate-100 text-sm sm:text-base max-w-xl">
                {t.footer}
              </p>
              <a
                href="https://canalboats.no"
                target="_blank"
                rel="noreferrer"
                data-testid="public-contact-cta"
              >
                <Button className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                  {t.contact_cta} →
                </Button>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionCard({ icon, label, count, tone = "gold", children }) {
  const borderTone = {
    gold: "border-[#BD984D]/40",
    cyan: "border-cyan-500/40",
    emerald: "border-emerald-500/40",
    teal: "border-[#0C6E7E]/50",
  }[tone];
  return (
    <Card
      className={`bg-[#152238]/80 backdrop-blur border ${borderTone} p-5 rounded-2xl text-white`}
    >
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div className="text-[10px] uppercase tracking-[0.32em] text-slate-300 font-semibold">
          {label}
        </div>
        <span className="ml-auto text-xs text-slate-400">{count}</span>
      </div>
      {children}
    </Card>
  );
}

function EmptyText({ children }) {
  return <p className="text-sm text-slate-400 italic">{children}</p>;
}

function BoatChips({ items, tone = "gold" }) {
  const cls = {
    gold: "bg-[#BD984D]/20 text-[#ECC974] border-[#BD984D]/40",
    cyan: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40",
    emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
    teal: "bg-[#0C6E7E]/25 text-cyan-100 border-[#0C6E7E]/50",
  }[tone];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((b) => (
        <span
          key={b.id || b.name}
          className={`px-3 py-1.5 rounded-full text-sm border ${cls}`}
          data-testid={`public-boat-chip-${b.id || b.name}`}
        >
          {b.name}
        </span>
      ))}
    </div>
  );
}

function BoatCards({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((b) => (
        <div
          key={b.id}
          className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 flex items-center gap-3"
          data-testid={`public-boat-card-${b.id}`}
        >
          <div className="h-10 w-10 rounded-full bg-[#BD984D]/25 border border-[#BD984D]/40 flex items-center justify-center shrink-0">
            <Ship className="w-4 h-4 text-[#ECC974]" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-white truncate">{b.name}</div>
            <div className="text-[11px] text-slate-400">
              {b.model || "Greenline"}
              {b.length_ft ? ` · ${b.length_ft}′` : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function WeekGrid({ data, lang, t }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" data-testid="public-week-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
            <th className="pr-4 py-2">Boat</th>
            {data.week_dates.map((d) => (
              <th key={d} className="px-2 py-2 text-center">
                {fmtDay(d, lang)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.week_availability.map((row) => (
            <tr
              key={row.id}
              className="border-t border-white/5"
              data-testid={`public-week-row-${row.id}`}
            >
              <td className="pr-4 py-2 font-medium text-white whitespace-nowrap">
                {row.name}
              </td>
              {data.week_dates.map((d) => {
                const isAvailable = row.available_dates.includes(d);
                return (
                  <td key={d} className="px-2 py-1 text-center">
                    <span
                      title={isAvailable ? t.available : t.booked}
                      className={`inline-block w-5 h-5 rounded-full ${
                        isAvailable
                          ? "bg-emerald-500/70 border border-emerald-300"
                          : "bg-red-500/40 border border-red-300/60"
                      }`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full bg-emerald-500/70 border border-emerald-300" />
          {t.available}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full bg-red-500/40 border border-red-300/60" />
          {t.booked}
        </span>
      </div>
    </div>
  );
}
